import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./style/landing.css";

const buildSearchParams = (query) => {
  const trimmed = String(query || "").trim();
  const params = new URLSearchParams();
  params.set("query", trimmed);

  if (/^\d{8}$/.test(trimmed)) {
    params.set("analyze", "1");
  } else {
    params.set("search", "1");
  }

  return params.toString();
};

function AppHomePage() {
  const [searchInput, setSearchInput] = useState("");
  const [showBillingSuccess, setShowBillingSuccess] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const billingSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
  }, []);

  useEffect(() => {
    if (billingSearchParams.get("billing") === "success") {
      setShowBillingSuccess(true);
      const nextParams = new URLSearchParams(location.search);
      nextParams.delete("billing");
      navigate(
        {
          pathname: location.pathname,
          search: nextParams.toString() ? `?${nextParams.toString()}` : "",
        },
        { replace: true }
      );
    }
  }, [billingSearchParams, location.pathname, location.search, navigate]);

  const handleCloseSuccess = () => {
    setShowBillingSuccess(false);
  };

  const handleAnalyze = (event) => {
    event.preventDefault();
    const trimmed = searchInput.trim();
    if (!trimmed) {
      setAnalyzeError("Pole je prázdné. Zadejte prosím IČO nebo název firmy pro spuštění analýzy.");
      return;
    }

    setAnalyzeError("");

    navigate(`/app/vyhledani-firmy?${buildSearchParams(trimmed)}`);
  };

  return (
    <>
      {showBillingSuccess && (
        <div className="billing-overlay" role="dialog" aria-live="polite">
          <div className="billing-overlay__card">
            <div className="billing-overlay__icon">✅</div>
            <h2>Platba proběhla úspěšně</h2>
            <p>Děkujeme za podporu! Kredity byly připsány k vašemu účtu.</p>
            <p className="billing-overlay__credits">
              Aktuální stav kreditů: <strong>{user?.credits ?? 0}</strong>
            </p>
            <button className="site-button site-button--primary" type="button" onClick={handleCloseSuccess}>
              Pokračovat
            </button>
          </div>
        </div>
      )}
      <div className="app-home">
      <header style={{ marginBottom: "3rem", textAlign: "center" }}>
        <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
          Vítejte zpět{user?.name ? `, ${user.name}` : ""} 👋
        </h1>
      </header>

      <section
        className="cta__card app-home__cta-card"
        style={{
          background: "white",
          padding: "2.5rem",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          marginBottom: "3rem",
          maxWidth: "1200px",
          marginLeft: "auto",
          marginRight: "auto",
          textAlign: "center",
          borderRadius: "12px",
        }}
      >
        <h2 style={{ marginBottom: "1rem", fontWeight:"bold", fontSize:"2rem" }}>Finanční analýza &amp; Benchmarking</h2>
        <p style={{ marginBottom: "1.5rem", color: "#555" }}>
          Zadejte IČO nebo název společnosti pro okamžitý výpočet <strong>tržní hodnoty společnosti</strong>.
        </p>

        <form className="app-home__search-form" onSubmit={handleAnalyze}>
          <input
            type="text"
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              if (analyzeError) {
                setAnalyzeError("");
              }
            }}
            placeholder="Např. 24698059 nebo Slevomat..."
            className="app-home__search-input"
          />
          <button className="button button--primary app-home__search-submit" type="submit">
            Analyzovat 🚀
          </button>
        </form>
        {analyzeError && (
          <p style={{ marginTop: "0.75rem", color: "#c11818", fontWeight: 600 }}>{analyzeError}</p>
        )}

        <div style={{ marginTop: "1rem", fontSize: "0.85rem", color: "#888" }}>
          Poznamka: Některé firmy nemají dostupné verejné údaje, což může ovlivnit výsledky analýzy.
        </div>
      </section>

      <h3 className="app-home__tools-heading" style={{ marginBottom: "1.5rem", color: "#333", borderLeft: "4px solid var(--accent)", paddingLeft: "10px" }}>
        Analytické nástroje trhu
      </h3>

      <div className="cards app-home__cards">
        <article
          style={{ cursor: "pointer", transition: "transform 0.2s", display: "flex", flexDirection: "column" }}
          onClick={() => navigate("/app/oborove-statistiky")}
          onMouseEnter={(event) => {
            event.currentTarget.style.transform = "translateY(-5px)";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <div className="app-home__card-header" style={{ marginBottom: "1rem" }}>
            <strong style={{ fontSize: "1.2rem" }}>📈 Statistiky odvětví</strong>
            <span className="app-home__card-badge" style={{ background: "#eef2ff", color: "var(--accent)", padding: "4px 8px", borderRadius: "4px", fontSize: "0.8rem" }}>Data 2016-2025</span>
          </div>
          <p>
            Detailní pohled na marže, likviditu a zadluženost podle klasifikace NACE.
            Sledujte historický vývoj a cyklické trendy v ekonomice.
          </p>
          <div style={{ marginTop: "auto", paddingTop: "1rem", color: "var(--accent)", fontWeight: "500" }}>
            Otevřít report →
          </div>
        </article>

        <article
          style={{ cursor: "pointer", transition: "transform 0.2s", display: "flex", flexDirection: "column" }}
          onClick={() => navigate("/app/benchmark-obory")}
          onMouseEnter={(event) => {
            event.currentTarget.style.transform = "translateY(-5px)";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <div className="app-home__card-header" style={{ marginBottom: "1rem" }}>
            <strong style={{ fontSize: "1.2rem" }}>🏆 Benchmark oborů</strong>
            <span className="app-home__card-badge" style={{ background: "#e6fffa", color: "#00b894", padding: "4px 8px", borderRadius: "4px", fontSize: "0.8rem" }}>Srovnání</span>
          </div>
          <p>
            Interaktivní srovnávač sektorů. Které obory mají nejvyšší návratnost kapitálu (ROE)?
            Kde je nejvyšší riziko bankrotu?
          </p>
          <div style={{ marginTop: "auto", paddingTop: "1rem", color: "var(--accent)", fontWeight: "500" }}>
            Porovnat sektory →
          </div>
        </article>


        <article
          style={{ cursor: "pointer", transition: "transform 0.2s", display: "flex", flexDirection: "column" }}
          onClick={() => navigate("/app/financni-analyza")}
          onMouseEnter={(event) => {
            event.currentTarget.style.transform = "translateY(-5px)";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <div className="app-home__card-header" style={{ marginBottom: "1rem" }}>
            <strong style={{ fontSize: "1.2rem" }}>🧾 Finanční analýza z PDF</strong>
            <span className="app-home__card-badge" style={{ background: "#fff4e6", color: "#b5651d", padding: "4px 8px", borderRadius: "4px", fontSize: "0.8rem" }}>Invite Only</span>
          </div>
          <p>
            Nahrajte výkazy, doplňte parametry a vygenerujte stručný výstup i
            odkaz na kompletní PDF report.
          </p>
          <div style={{ marginTop: "auto", paddingTop: "1rem", color: "var(--accent)", fontWeight: "500" }}>
            Otevřít nástroj →
          </div>
        </article>

      </div>
      </div>
    </>
  );
}

export default AppHomePage;
